import { apiClient } from '@/api/client';
import type { SpringPage } from '@/types/students';
import type {
  SubmissionDetail,
  SubmissionListParams,
  SubmissionRow,
} from '@/types/submissions';

/**
 * Xuất Excel / đồng bộ Sheets đọc hết bài làm của bộ lọc rồi dựng file — lớp lớn
 * (vd 6.500 bài, ~28 MB chữ) mất hàng chục giây, vượt xa timeout 15s mặc định.
 */
const HEAVY_TIMEOUT_MS = 5 * 60_000;

function buildListQuery(params: SubmissionListParams): URLSearchParams {
  const q = new URLSearchParams();
  if (params.communityId !== undefined && params.communityId !== null) {
    q.set('communityId', String(params.communityId));
  }
  if (params.courseId !== undefined && params.courseId !== null) {
    q.set('courseId', String(params.courseId));
  }
  if (params.assignmentIds && params.assignmentIds.length > 0) {
    // Spring binds repeated query params to List<Long>.
    for (const id of params.assignmentIds) {
      q.append('assignmentIds', String(id));
    }
  }
  if (params.cohorts && params.cohorts.length > 0) {
    for (const c of params.cohorts) {
      q.append('cohorts', String(c));
    }
  }
  if (params.q && params.q.trim() !== '') {
    q.set('q', params.q.trim());
  }
  if (params.dueDateFrom) q.set('dueDateFrom', params.dueDateFrom);
  if (params.dueDateTo) q.set('dueDateTo', params.dueDateTo);
  if (params.status) q.set('status', params.status);
  if (params.page !== undefined) q.set('page', String(params.page));
  if (params.size !== undefined) q.set('size', String(params.size));
  if (params.sort) q.set('sort', params.sort);
  return q;
}

export async function listSubmissions(
  params: SubmissionListParams,
): Promise<SpringPage<SubmissionRow>> {
  const { data } = await apiClient.get<SpringPage<SubmissionRow>>(
    `/admin/submissions`,
    { params: buildListQuery(params) },
  );
  return data;
}

export async function getSubmission(
  submissionId: number,
): Promise<SubmissionDetail> {
  const { data } = await apiClient.get<SubmissionDetail>(
    `/admin/submissions/${submissionId}`,
  );
  return data;
}

/**
 * Download the current filter result as an xlsx blob. Mirrors the list query
 * params (page/size are ignored server-side for export).
 */
export async function exportSubmissionsXlsx(
  params: SubmissionListParams,
): Promise<Blob> {
  const response = await apiClient.get(`/admin/submissions/export`, {
    params: buildListQuery(params),
    responseType: 'blob',
    timeout: HEAVY_TIMEOUT_MS,
  });
  return response.data as Blob;
}

/**
 * Sync the current filter result into a Google Sheets tab. Backend uses a
 * service-account credential and OVERWRITES the target range — the sheet
 * always reflects the latest snapshot for the chosen filter.
 *
 * <p>Same filter context as the Excel export. {@code sheetUrl} is the full
 * Google Sheets URL; the backend parses out the spreadsheet ID. Returns a
 * confirmation payload so the UI can show row count + a "Mở sheet" link.
 */
export interface SyncSheetsTabResult {
  name: string;
  rowCount: number;
}

export interface SyncSheetsResult {
  spreadsheetId: string;
  spreadsheetUrl: string;
  /**
   * 1 entry per tab written. Single-tab mode (autoCreate=false) returns 1
   * entry; split-by-dueDate mode returns 1 per unique due date the filter
   * touched. Empty filter result → backend returns empty array.
   */
  tabs: SyncSheetsTabResult[];
  totalRowCount: number;
  syncedAt: string;
}

export async function syncSubmissionsToGoogleSheets(
  params: SubmissionListParams,
  body: { sheetUrl: string; sheetName?: string; autoCreate?: boolean },
): Promise<SyncSheetsResult> {
  const response = await apiClient.post<SyncSheetsResult>(
    `/admin/submissions/sync-google-sheets`,
    body,
    { params: buildListQuery(params), timeout: HEAVY_TIMEOUT_MS },
  );
  return response.data;
}

/** Cấu hình tự động đồng bộ (30 phút/lần tới ngày dừng) — `/admin/sheet-auto-syncs`. */
export interface SheetAutoSync {
  id: number;
  communityId: number;
  sheetUrl: string;
  sheetName: string | null;
  autoCreate: boolean;
  courseId: number | null;
  assignmentIds: number[] | null;
  studentSearch: string | null;
  dueDateFrom: string | null;
  dueDateTo: string | null;
  cohorts: number[] | null;
  status: string | null;
  /** yyyy-MM-dd, chạy tới hết ngày này. */
  endDate: string;
  enabled: boolean;
  lastRunAt: string | null;
  lastStatus: 'OK' | 'SKIPPED' | 'ERROR' | null;
  lastMessage: string | null;
  lastRowCount: number | null;
  consecutiveFailures: number;
}

export async function listAutoSyncs(communityId: number): Promise<SheetAutoSync[]> {
  const { data } = await apiClient.get<SheetAutoSync[]>(`/admin/sheet-auto-syncs`, {
    params: { communityId },
  });
  return data;
}

/** Lưu đúng bộ lọc đang chọn (như Excel export / đồng bộ tay) thành đồng bộ tự động. */
export async function createAutoSync(
  params: SubmissionListParams,
  body: { sheetUrl: string; sheetName?: string; autoCreate: boolean; endDate: string },
): Promise<SheetAutoSync> {
  const { data } = await apiClient.post<SheetAutoSync>(`/admin/sheet-auto-syncs`, {
    ...body,
    communityId: params.communityId,
    courseId: params.courseId,
    assignmentIds: params.assignmentIds,
    studentSearch: params.q?.trim() || undefined,
    dueDateFrom: params.dueDateFrom,
    dueDateTo: params.dueDateTo,
    cohorts: params.cohorts,
    status: params.status,
  });
  return data;
}

export async function updateAutoSync(
  id: number,
  body: { enabled?: boolean; endDate?: string },
): Promise<SheetAutoSync> {
  const { data } = await apiClient.patch<SheetAutoSync>(`/admin/sheet-auto-syncs/${id}`, body);
  return data;
}

export async function deleteAutoSync(id: number): Promise<void> {
  await apiClient.delete(`/admin/sheet-auto-syncs/${id}`);
}

/** Chạy ngay, ghi lại sheet kể cả khi không có gì mới. */
export async function runAutoSync(id: number): Promise<SheetAutoSync> {
  const { data } = await apiClient.post<SheetAutoSync>(`/admin/sheet-auto-syncs/${id}/run`, undefined, {
    timeout: HEAVY_TIMEOUT_MS,
  });
  return data;
}
